import { useCallback, useEffect, useRef, useState } from "react";

const SDK_SRC = "https://embed.cloudflarestream.com/embed/sdk.latest.js";
let sdkPromise = null;

function loadSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.Stream) return Promise.resolve(window.Stream);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SDK_SRC;
      s.async = true;
      s.onload = () => (window.Stream ? resolve(window.Stream) : reject(new Error("Stream SDK missing")));
      s.onerror = () => reject(new Error("Stream SDK failed to load"));
      document.head.appendChild(s);
    });
  }
  return sdkPromise;
}

/**
 * Control a Cloudflare Stream <iframe> (seek + current time) via the official player SDK.
 * Falls back to reloading the iframe with ?startTime= when the SDK is unavailable.
 */
export function useStreamPlayer(src) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const el = iframeRef.current;
    if (!el) return undefined;
    let cancelled = false;
    let player = null;
    setCurrentTime(0);
    const onTime = () => { if (player) setCurrentTime(player.currentTime || 0); };
    loadSdk().then((Stream) => {
      if (cancelled) return;
      player = Stream(el);
      playerRef.current = player;
      player.addEventListener("timeupdate", onTime);
    }).catch(() => {});
    return () => {
      cancelled = true;
      try { player?.removeEventListener("timeupdate", onTime); } catch (e) { /* noop */ }
      playerRef.current = null;
    };
  }, [src]);

  const seek = useCallback((seconds) => {
    const el = iframeRef.current;
    const p = playerRef.current;
    if (p) {
      try { p.currentTime = seconds; p.play?.(); setCurrentTime(seconds); return; } catch (e) { /* fall through */ }
    }
    if (el) {
      const url = new URL(el.src);
      url.searchParams.set("startTime", `${Math.max(0, Math.floor(seconds))}s`);
      url.searchParams.set("autoplay", "true");
      el.src = url.toString();
      setCurrentTime(seconds);
    }
  }, []);

  return { iframeRef, seek, currentTime };
}

export default useStreamPlayer;
